import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { VALID_VARIABLE_NAME_REGEX, formatVariableSyntax, parseTemplateVariables } from '../lib/template';

interface AddVariablePopoverProps {
  content: string;
  onAdd: (syntax: string) => void;
  onClose: () => void;
}

export default function AddVariablePopover({
  content,
  onAdd,
  onClose,
}: Readonly<AddVariablePopoverProps>): JSX.Element {
  const [name, setName] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [hint, setHint] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && VALID_VARIABLE_NAME_REGEX.test(trimmedName);
  const existingVariables = parseTemplateVariables(content);
  const isDuplicate = existingVariables.some((v) => v.name === trimmedName);

  const canAdd = isNameValid && !isDuplicate;

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleAdd = () => {
    if (!canAdd) {
      return;
    }

    const syntax = formatVariableSyntax(
      trimmedName,
      defaultValue.trim() || undefined,
      hint.trim() || undefined,
    );
    onAdd(syntax);
  };

  const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleAdd();
  };

  const nameError = trimmedName.length > 0 && !isNameValid
    ? 'Only letters, numbers, and underscores'
    : isDuplicate
      ? 'Variable already exists in prompt'
      : null;

  return (
    <div
      ref={popoverRef}
      className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-border bg-popover p-3 shadow-md"
    >
      <div className="flex flex-col gap-2.5">
        <p className="text-xs font-semibold text-foreground">Add Variable</p>

        <div className="flex flex-col gap-1">
          <Label htmlFor="variable-name">Name *</Label>
          <Input
            ref={nameInputRef}
            id="variable-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="e.g. topic"
          />
          {nameError ? (
            <p className="text-[11px] text-destructive">{nameError}</p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="variable-default">Default value</Label>
          <Input
            id="variable-default"
            value={defaultValue}
            onChange={(event) => setDefaultValue(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Optional"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="variable-hint">Hint text</Label>
          <Input
            id="variable-hint"
            value={hint}
            onChange={(event) => setHint(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder="Optional"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={!canAdd} onClick={handleAdd}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
